import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { auth } from '../lib/firebase';
import { signOut } from 'firebase/auth';
import { 
  Menu,
  X,
  LogOut,
  User as UserIcon,
  ShoppingCart
} from 'lucide-react';

const CoursedeeNavbar: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { itemCount } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    // Navigate to home page first, then scroll
    navigate('/', { state: { scrollTo: sectionId } });
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="fixed top-0 w-full bg-gray-900/95 backdrop-blur-sm z-50 border-b border-purple-800/30" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center group cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="/logo-edumi-ai.jpg" 
              alt="AIForYou Logo"
              className="h-12 w-auto transform group-hover:scale-105 transition-all duration-300"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('home')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              หน้าแรก
            </button>
            <button 
              onClick={() => scrollToSection('courses')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              คอร์สทั้งหมด
            </button>
            <button 
              onClick={() => scrollToSection('howto')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              วิธีใช้งาน
            </button>
            <button 
              onClick={() => scrollToSection('reviews')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              รีวิว
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-gray-300 hover:text-white transition-colors"
            >
              ติดต่อเรา
            </button>
            
            {/* Shopping Cart Icon */}
            <button 
              onClick={() => navigate('/cart')}
              className="relative text-gray-300 hover:text-white transition-colors"
            >
              <ShoppingCart size={24} />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </button>
            
            {/* User Info & Logout / Login - Desktop */}
            {user ? (
              <div className="flex items-center gap-3 ml-4 pl-4 border-l border-purple-800/50">
                <div className="flex items-center gap-2 text-white">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg">
                    {user.username?.[0]?.toUpperCase() || user.email?.[0].toUpperCase()}
                  </div>
                  <span className="text-sm font-semibold">{user.username || user.email}</span>
                </div>
                <button
                  onClick={() => navigate('/coursedee/dashboard')}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
                >
                  <UserIcon className="w-4 h-4" />
                  <span className="text-sm">Dashboard</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">ออกจากระบบ</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-purple-800/50">
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-2 text-gray-200 hover:text-white px-4 py-2.5 rounded-lg transition-all duration-200 font-semibold"
                >
                  <UserIcon className="w-4 h-4" />
                  <span className="text-sm">เข้าสู่ระบบ</span>
                </button>
                <button
                  onClick={() => navigate('/login?mode=register')}
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-2.5 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
                >
                  <span className="text-sm">สมัครฟรี</span>
                </button>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 space-y-2">
            <button 
              onClick={() => scrollToSection('home')}
              className="block w-full text-left py-2 text-gray-300 hover:text-white transition-colors"
            >
              หน้าแรก
            </button>
            <button 
              onClick={() => scrollToSection('courses')}
              className="block w-full text-left py-2 text-gray-300 hover:text-white transition-colors"
            >
              คอร์สทั้งหมด
            </button>
            <button 
              onClick={() => scrollToSection('howto')}
              className="block w-full text-left py-2 text-gray-300 hover:text-white transition-colors"
            >
              วิธีใช้งาน
            </button>
            <button 
              onClick={() => scrollToSection('reviews')}
              className="block w-full text-left py-2 text-gray-300 hover:text-white transition-colors"
            >
              รีวิว
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left py-2 text-gray-300 hover:text-white transition-colors"
            >
              ติดต่อเรา
            </button>
            
            {/* Mobile Cart */}
            <button 
              onClick={() => {
                navigate('/cart');
                setIsMenuOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left py-2 text-gray-300 hover:text-white transition-colors"
            >
              <ShoppingCart size={20} />
              ตะกร้า {itemCount > 0 && `(${itemCount})`}
            </button>
            
            {/* Mobile User Actions */}
            {user ? (
              <>
                <button
                  onClick={() => {
                    navigate('/coursedee/dashboard');
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all duration-200"
                >
                  <UserIcon className="w-4 h-4" />
                  Dashboard
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all duration-200"
                >
                  <LogOut className="w-4 h-4" />
                  ออกจากระบบ
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full border border-purple-500/50 text-gray-200 hover:text-white px-4 py-2 rounded-lg transition-all duration-200"
                >
                  <UserIcon className="w-4 h-4" />
                  เข้าสู่ระบบ
                </button>
                <button
                  onClick={() => {
                    navigate('/login?mode=register');
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-2 w-full justify-center bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg transition-all duration-200 font-semibold"
                >
                  สมัครฟรี
                </button>
              </>
            )}
          </nav>
        )}
      </div>
    </header>
  );
};

export default CoursedeeNavbar;
